import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { FileService } from '@core/services/file.service';
import {
  FileConfig,
  FileUpload,
  IFileConfig,
  IValidationMessage,
} from '@models/file';
import { RequiredValidation } from '@utils/validation/required-validation';
import { UploadValidation } from '@utils/validation/upload-validation';

@Component({
  selector: 'app-audio-uploader',
  template: `
    <app-base-uploader
      [fileConfig]="fileConfig"
      [fileListFormControl]="fileListFormControl"
      [form]="form"
      [validationMessageList]="validationMessageList"
      [disabled]="disabled"
      (emitFileSelect)="onFileSelect($event)"
      (fileUploadsEmitter)="fileUploadsEmitter.emit($event)"
      [required]="required"
    ></app-base-uploader>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioUploaderComponent implements OnInit {
  @Input() public fileListFormControlName: string = 'formValue';
  /**
   * The uploader requires the parent component to set up the form that
   * will contain the `FormControl` for the `FileList`.
   */
  @Input() public form: UntypedFormGroup;
  @Output() fileUploadsEmitter: EventEmitter<FileUpload[]> = new EventEmitter();
  @Input() public disabled: boolean = false;
  @Input() public required: boolean = false;
  @Output() public emitFileValid = new EventEmitter<boolean>();

  public fileConfig = new FileConfig({
    allowedFileExtensionList: ['mp3', 'wav', 'flac', 'mp4', 'm4a'],
    allowedMimeTypeList: [
      'audio/mp3',
      'audio/wav',
      'audio/flac',
      'audio/mp4',
      'audio/x-m4a',
    ],
    canUploadMultiple: false,
  });
  public validationMessageList: IValidationMessage[] = [];

  constructor(
    private fileService: FileService,
    private fb: UntypedFormBuilder,
  ) {}

  public ngOnInit(): void {
    if (!this.form) {
      this.form = this.fb.group({
        formValue: new UntypedFormControl('', Validators.required),
      });
    }
    this.setFileListFormControlValidation(this.fileConfig);
  }

  public get fileListFormControl(): AbstractControl {
    return this.form.get(this.fileListFormControlName);
  }

  public onFileSelect(fileList: FileList): void {
    if (fileList?.length > 0) {
      this.validationMessageList = this.validateFiles(fileList);
      this.fileListFormControl.setValue(fileList);
      this.validationMessageList.length
        ? this.emitFileValid.emit(true)
        : this.emitFileValid.emit(false);
    }
  }

  private setFileListFormControlValidation(fileConfig: IFileConfig): void {
    this.fileListFormControl?.setValidators([
      UploadValidation.validateFiles(fileConfig),
    ]);
    this.fileListFormControl?.updateValueAndValidity();
  }

  private validateFiles(fileList: FileList): IValidationMessage[] {
    return this.fileService.buildValidationSuccessMessageList(
      fileList,
      this.fileConfig,
    );
  }
}
